const API_BASE = process.env.REACT_APP_API_URL || "";

function getToken() {
  try {
    return localStorage.getItem("asv_token");
  } catch {
    return null;
  }
}

function setToken(token) {
  try {
    if (token) localStorage.setItem("asv_token", token);
    else localStorage.removeItem("asv_token");
  } catch {
    /* ignore */
  }
}

async function api(path, options = {}) {
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* non-JSON response (e.g. old backend or proxy HTML error page) */
  }
  if (!res.ok) {
    const fallback =
      res.status === 404
        ? "API endpoint not found — restart the backend server"
        : res.status === 401
          ? "Authentication required"
          : res.status === 403
            ? "Admin access required"
            : text.startsWith("<!")
              ? `Request failed (${res.status})`
              : "Request failed";
    const err = new Error(data.error || fallback);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const authApi = {
  register: (body) => api("/api/auth/register", { method: "POST", body: JSON.stringify(body) }),
  login: (body) => api("/api/auth/login", { method: "POST", body: JSON.stringify(body) }),
  me: () => api("/api/auth/me"),
  googleStatus: () => api("/api/auth/google/status"),
  startGoogleLogin: () => {
    window.location.href = `${API_BASE}/api/auth/google`;
  },
  logout: () => setToken(null),
  setToken,
  getToken,
};

export { API_BASE, getToken };

async function downloadCsv(path, filename) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const text = await res.text();
  if (!res.ok) {
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      /* csv error body */
    }
    throw new Error(data.error || "Export failed");
  }
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const billingApi = {
  plans: () => api("/api/billing/plans"),
  config: () => api("/api/billing/config"),
  status: () => api("/api/billing/status"),
  subscribe: (planId) =>
    api("/api/billing/subscribe", { method: "POST", body: JSON.stringify({ planId }) }),
  completeSubscription: (subscriptionId) =>
    api("/api/billing/complete-subscription", {
      method: "POST",
      body: JSON.stringify({ subscriptionId }),
    }),
  purchase: (planId) =>
    api("/api/billing/purchase", { method: "POST", body: JSON.stringify({ planId }) }),
};

export const validationApi = {
  list: () => api("/api/validations"),
  validate: (idea) => api("/api/validate", { method: "POST", body: JSON.stringify({ idea }) }),
  demoValidate: (idea) =>
    api("/api/validate/demo", { method: "POST", body: JSON.stringify({ idea }) }),
  toggleStar: (id) => api(`/api/validations/${id}/star`, { method: "PATCH" }),
  delete: (id) => api(`/api/validations/${id}`, { method: "DELETE" }),
};

export const developmentApi = {
  list: (validationId) => api(`/api/develop/${validationId}`),
  generate: (featureType, body) =>
    api(`/api/develop/${featureType}`, { method: "POST", body: JSON.stringify(body) }),
};

export const bugReportsApi = {
  submit: (body) => api("/api/bug-reports", { method: "POST", body: JSON.stringify(body) }),
};

export const adminApi = {
  stats: () => api("/api/admin/stats"),
  users: (q = "") => api(`/api/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  user: (id) => api(`/api/admin/users/${id}`),
  updateUser: (id, body) =>
    api(`/api/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  syncUserStripe: (id) => api(`/api/admin/users/${id}/sync-stripe`, { method: "POST" }),
  cancelUserStripe: (id) => api(`/api/admin/users/${id}/cancel-stripe`, { method: "POST" }),
  subscriptions: () => api("/api/admin/subscriptions"),
  deleteUser: (id) => api(`/api/admin/users/${id}`, { method: "DELETE" }),
  validations: () => api("/api/admin/validations"),
  purchases: ({ status, plan } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (plan) params.set("plan", plan);
    const qs = params.toString();
    return api(`/api/admin/purchases${qs ? `?${qs}` : ""}`);
  },
  refundPurchase: (id) => api(`/api/admin/purchases/${id}/refund`, { method: "POST" }),
  bugReports: ({ status } = {}) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return api(`/api/admin/bug-reports${qs}`);
  },
  updateBugReport: (id, body) =>
    api(`/api/admin/bug-reports/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  admins: () => api("/api/admin/admins"),
  saveAdmins: (emails) =>
    api("/api/admin/admins", { method: "PUT", body: JSON.stringify({ emails }) }),
  exportUsersCsv: () => downloadCsv("/api/admin/export/users.csv", "users.csv"),
  exportPurchasesCsv: ({ status, plan } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (plan) params.set("plan", plan);
    const qs = params.toString();
    return downloadCsv(`/api/admin/export/purchases.csv${qs ? `?${qs}` : ""}`, "purchases.csv");
  },
  aiSettings: () => api("/api/admin/ai-settings"),
  aiUsage: (days) => {
    const qs = days ? `?days=${encodeURIComponent(days)}` : "";
    return api(`/api/admin/ai-usage${qs}`);
  },
  saveAiSettings: (body) =>
    api("/api/admin/ai-settings", { method: "PUT", body: JSON.stringify(body) }),
  testAiSettings: (body) =>
    api("/api/admin/ai-settings/test", { method: "POST", body: JSON.stringify(body) }),
  googleAuthSettings: () => api("/api/admin/google-auth-settings"),
  saveGoogleAuthSettings: (body) =>
    api("/api/admin/google-auth-settings", { method: "PUT", body: JSON.stringify(body) }),
  stripeSettings: () => api("/api/admin/stripe-settings"),
  saveStripeSettings: (body) =>
    api("/api/admin/stripe-settings", { method: "PUT", body: JSON.stringify(body) }),
  testStripeSettings: (body) =>
    api("/api/admin/stripe-settings/test", { method: "POST", body: JSON.stringify(body) }),
  backupSettings: () => api("/api/admin/backup-settings"),
  saveBackupSettings: (body) =>
    api("/api/admin/backup-settings", { method: "PUT", body: JSON.stringify(body) }),
  testBackupSettings: (body) =>
    api("/api/admin/backup-settings/test", { method: "POST", body: JSON.stringify(body) }),
  runBackup: (body) =>
    api("/api/admin/backup-settings/run", {
      method: "POST",
      body: JSON.stringify(body || {}),
    }),
};
